import React from "react";
import { ResponsiveTable } from "../ResponsiveTable";
import {
  Box,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { MULISH } from "../../WireGuardTunnels/VpnDebugger/vpnDebugConstants";
import { useTroubleshootingStore } from "../troubleshootingStore/troubleshootingStore";
import {
  Finding,
  SymptomId,
  TroubleshootingTab,
} from "../troubleshootingInterfaces";
import {
  Card,
  CardHeader,
  CELL_SX,
  HEAD_CELL_SX,
  Hint,
  KindChip,
  OwnerPill,
  Pill,
  SampleChip,
  SiteLink,
  linkifySite,
  Mono,
  siteAboutPath,
  TS_COLORS,
  TsButton,
} from "../TroubleshootingUi";
import { useScopeLabel } from "../TroubleshootingUtils/useScope";
import {
  AlertsReceivedCard,
  alertsReceivedSentence,
} from "./AlertsReceivedCard";

const SYMPTOMS: { id: SymptomId; label: string }[] = [
  { id: "none", label: "Not receiving any alarms" },
  { id: "late", label: "Alarms arriving late" },
  { id: "new", label: "New site or server won't connect" },
  { id: "cam", label: "One camera is offline" },
  { id: "armed", label: "Site stuck armed or a schedule didn't run" },
];

/** Where an answer sends the customer next. */
export interface AnswerContext {
  goTo: (tab: TroubleshootingTab) => void;
}

const TabButton = ({
  tab,
  ctx,
  children,
}: {
  tab: TroubleshootingTab;
  ctx: AnswerContext;
  children: React.ReactNode;
}) => (
  <TsButton
    variant="ghost"
    size="sm"
    onClick={() => ctx.goTo(tab)}
    data-testid={`symptom-go-${tab}`}
  >
    {children}
  </TsButton>
);

const Steps = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="ol"
    sx={{
      m: 0,
      mt: 1,
      pl: 2.5,
      display: "flex",
      flexDirection: "column",
      gap: 0.75,
    }}
  >
    {children}
  </Box>
);

/**
 * The walkthrough for a symptom. Deliberately a manual, step-by-step path
 * through the chain alarms travel — site armed → path up → images/alerts in
 * → receiver accepting — pointing at the tab that proves each step, rather
 * than a verdict computed from camera and connector health (that is Camera
 * Health's job, and the two were overlapping). The one live fact that leads
 * is whether alerts are being processed for the scope right now.
 */
export const answerFor = (
  symptom: SymptomId,
  ctx: AnswerContext
): React.ReactNode => {
  switch (symptom) {
    case "none":
      return (
        <>
          <b>
            Work down the chain an alarm travels; stop at the first step that
            fails.
          </b>
          <Steps>
            <li>
              <b>Is the site armed and active?</b> A disarmed or inactive site
              raises no alarms, whatever the network looks like. Check its
              schedule or arm it on the site page.
            </li>
            <li>
              <b>Is the path to the site up?</b> Pick the site on the
              Connections tab and run the Connectivity analyzer against the
              address we dial; a WireGuard site also shows its peer on the
              WireGuard tab. A port that does not answer, or a stale handshake,
              is the fault, and the fix is on the network side.{" "}
              <TabButton tab="connections" ctx={ctx}>
                Open Connections
              </TabButton>{" "}
              <TabButton tab="wireguard" ctx={ctx}>
                Open WireGuard
              </TabButton>
            </li>
            <li>
              <b>Does your site email images (SMTP)?</b> Then the question is
              whether the emails reach us and name the right camera: send a test
              email and check the filename rule on the SMTP Cameras tab.{" "}
              <TabButton tab="smtp-cameras" ctx={ctx}>
                Open SMTP Cameras
              </TabButton>
            </li>
            <li>
              <b>Is your alarm receiver accepting?</b> Send a test alarm from
              the Alarm Receivers tab. <i>Refused</i> or <i>Unreachable</i> puts
              the fix on the receiver (restart the service, check its
              allow-list); <i>Accepted</i> means the whole chain works and the
              alarm is being filtered inside your VMS rules or operator client.{" "}
              <TabButton tab="alarm-receivers" ctx={ctx}>
                Open Alarm Receivers
              </TabButton>
            </li>
          </Steps>
        </>
      );
    case "late":
      return (
        <>
          <b>Late alarms are almost always the receiver, not the detection.</b>
          <Steps>
            <li>
              On the Alarm Receivers tab, read the time of the last accepted
              alarm and the failures in the last hour for your receiver. A
              receiver that stops accepting makes deliveries queue while we
              retry; once it listens again the backlog clears in minutes.{" "}
              <TabButton tab="alarm-receivers" ctx={ctx}>
                Open Alarm Receivers
              </TabButton>
            </li>
            <li>
              If the receiver is healthy, check that server&rsquo;s CPU, memory
              and logs before rebooting the whole box: slow acceptance usually
              means it is under load.
            </li>
            <li>
              If the delay is on the way in (images arriving late), run the
              Connectivity analyzer on the Connections tab and look at the reply
              times: a path that answers in seconds instead of milliseconds is
              being throttled somewhere on the network.
            </li>
          </Steps>
        </>
      );
    case "new":
      return (
        <>
          Actuate reaches a site one of three ways: over the internet to a
          public address (port forwarding on your router), through a WireGuard
          tunnel, or through an IPsec site-to-site VPN. Sites that email images
          (SMTP) connect to us instead. A new server or camera has to be
          reachable on the way its site is set up: its public address and port
          forwarded; or its address inside the WireGuard tunnel&rsquo;s networks
          (WireGuard tab); or inside the VPN&rsquo;s routes. To see which step
          fails, pick the site on the Connections tab and run the Connectivity
          analyzer against the new address. If the address is outside your
          tunnel or VPN networks, send it to us; adding it is a change on
          Actuate&rsquo;s side.{" "}
          <TabButton tab="connections" ctx={ctx}>
            Open Connections
          </TabButton>
        </>
      );
    case "cam":
      return (
        <>
          <b>Start with the Connectivity analyzer on the Connections tab.</b>{" "}
          Enter the camera&rsquo;s IP and its RTSP and HTTP ports. A port with{" "}
          <b>no reply</b> is behind most offline cameras and usually means a
          firewall or NAT rule on your side is dropping the traffic. If every
          port is open, run the <b>Credential test</b> right below it; a 401
          from the device is the second most common cause. Only then look at
          Camera Health for that camera.{" "}
          <TabButton tab="connections" ctx={ctx}>
            Open Connections
          </TabButton>
        </>
      );
    case "armed":
      return (
        <>
          <b>This is not a connection problem.</b> To find out why the site
          armed or disarmed when it did, open the site page: the Schedules tab
          shows the arm and disarm times, including any holiday schedule that
          overrides them, and Action logs show who armed or disarmed it and
          when. A site whose schedule did not run stays in its last state until
          the next scheduled time or until someone arms or disarms it.
        </>
      );
    default:
      return null;
  }
};

const AffectedTable = ({ finding }: { finding: Finding }) => {
  const { sites } = useTroubleshootingStore();
  const rows = finding.affectedSiteIds.map(
    (id) =>
      sites.find((s) => s.id === id) || { id, name: `Site ${id}`, groupId: 0 }
  );
  return (
    <Box
      sx={{
        mt: 1.5,
        border: `1px solid ${TS_COLORS.line}`,
        borderRadius: "4px",
        overflow: "auto",
      }}
    >
      <ResponsiveTable>
        <TableHead>
          <TableRow>
            <TableCell sx={HEAD_CELL_SX}>Site</TableCell>
            <TableCell sx={HEAD_CELL_SX}>Connects via</TableCell>
            <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((site) => (
            <TableRow key={site.id}>
              <TableCell sx={CELL_SX}>
                <SiteLink siteId={site.id}>{site.name}</SiteLink>
              </TableCell>
              <TableCell sx={CELL_SX}>
                {finding.connectionKind ? (
                  <KindChip kind={finding.connectionKind} />
                ) : (
                  <Pill tone="grey">Alarm receiver</Pill>
                )}
              </TableCell>
              <TableCell sx={CELL_SX}>
                {finding.kind === "connection"
                  ? "Alarms queued"
                  : finding.kind === "smtp"
                  ? "Images unidentified"
                  : finding.kind === "unreachable"
                  ? "No alarms generated"
                  : finding.kind === "arming"
                  ? "Not armed"
                  : finding.kind === "camera"
                  ? "Slow live view"
                  : "Alarms failing"}
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell sx={CELL_SX} colSpan={3}>
                <Hint>
                  We can&rsquo;t link this to specific sites yet (see the API
                  gaps note).
                </Hint>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </ResponsiveTable>
    </Box>
  );
};

export const FindingCard = ({
  finding,
  onCopy,
  onTestAlarm,
  isSample,
}: {
  finding: Finding;
  onCopy: () => void;
  onTestAlarm?: (receiverId: string) => void;
  isSample: boolean;
}) => {
  const {
    openAffected,
    toggleAffected,
    setTab,
    openTabForSite,
    setWgSubTab,
    setPendingDebugTunnelId,
    setS2sCheckOpen,
    requestProbe,
    siteDetail,
  } = useTroubleshootingStore();
  const open = Boolean(openAffected[finding.id]);
  const good = finding.severity === "good";
  const info = finding.severity === "info";
  return (
    <Box
      data-testid={`finding-${finding.id}`}
      sx={{
        display: "grid",
        // Phones: the metric drops under the text instead of squeezing it.
        gridTemplateColumns: {
          xs: "32px minmax(0, 1fr)",
          md: "44px minmax(0, 1fr) auto",
        },
        gap: { xs: 1.5, md: 2 },
        p: { xs: 1.75, md: 2.5 },
        borderTop: `1px solid ${TS_COLORS.line}`,
        backgroundColor: good ? "#f6fbf7" : info ? "#f7f9fc" : "#fffaf5",
        "&:first-of-type": { borderTop: 0 },
      }}
    >
      {good ? (
        <CheckCircleIcon
          sx={{ fontSize: { xs: 30, md: 40 }, color: TS_COLORS.green }}
        />
      ) : info ? (
        <InfoOutlinedIcon
          sx={{ fontSize: { xs: 30, md: 40 }, color: TS_COLORS.navy }}
        />
      ) : (
        <WarningAmberRoundedIcon
          sx={{ fontSize: { xs: 30, md: 40 }, color: TS_COLORS.orange }}
        />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h3"
          sx={{
            ...MULISH,
            fontWeight: 800,
            fontSize: 19,
            lineHeight: 1.25,
            color: TS_COLORS.navy,
          }}
        >
          {linkifySite(finding.title, finding.siteId, finding.siteName)}
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            alignItems: "center",
            mt: 1,
          }}
        >
          {good ? (
            <Pill tone="ok">Healthy</Pill>
          ) : finding.kind === "healthy" ? (
            <Pill tone="warn">Not confirmed</Pill>
          ) : (
            <OwnerPill owner={finding.owner} />
          )}
          {finding.global ? (
            <Pill tone="navy">Global: affects every site on this receiver</Pill>
          ) : finding.connectionKind ? (
            <KindChip kind={finding.connectionKind} />
          ) : finding.kind === "receiver" ? (
            <Pill tone="grey">Alarm receiver</Pill>
          ) : null}
          {finding.since && <Hint>Since {finding.since}</Hint>}
          {isSample && <SampleChip />}
        </Box>
        <Typography
          sx={{ ...MULISH, fontSize: 15, mt: 1.25, maxWidth: "78ch" }}
        >
          {finding.body}
        </Typography>
        {finding.detailLine ? (
          <Box
            sx={{ mt: 1, maxWidth: "78ch", wordBreak: "break-word" }}
            data-testid="finding-detail-line"
          >
            <Mono>{finding.detailLine}</Mono>
          </Box>
        ) : null}
        {finding.steps.length > 0 && (
          <Box
            component="ol"
            sx={{
              ...MULISH,
              fontSize: 15,
              m: "12px 0 0",
              pl: 2.5,
              maxWidth: "78ch",
              "& li": { my: 0.5 },
            }}
          >
            {finding.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.75 }}>
          {finding.actions.includes("test-alarm") && finding.receiverId && (
            <TsButton
              onClick={() => onTestAlarm?.(finding.receiverId as string)}
            >
              Send test alarm
            </TsButton>
          )}
          {finding.actions.includes("affected") && (
            <TsButton
              variant="outline"
              onClick={() => toggleAffected(finding.id)}
            >
              {open ? "Hide" : "View"} affected sites (
              {finding.affectedSiteIds.length})
            </TsButton>
          )}
          {finding.actions.includes("wg-debug") && finding.tunnelId != null && (
            <TsButton
              variant="outline"
              color={TS_COLORS.wg}
              onClick={() => {
                setPendingDebugTunnelId(finding.tunnelId ?? null);
                setWgSubTab("debugger");
                setTab("wireguard");
              }}
            >
              Open in WireGuard Debugger
            </TsButton>
          )}
          {finding.actions.includes("s2s-check") && finding.connectionId && (
            <TsButton
              variant="outline"
              onClick={() => {
                setS2sCheckOpen(finding.connectionId as string, true);
                setTab("connections");
              }}
            >
              Open connection check
            </TsButton>
          )}
          {finding.actions.includes("open-site") && finding.siteId != null && (
            <TsButton
              onClick={() =>
                window.open(
                  siteAboutPath(finding.siteId as number),
                  "_blank",
                  "noopener"
                )
              }
              data-testid={`finding-open-site-${finding.id}`}
            >
              Open site page
            </TsButton>
          )}
          {finding.actions.includes("probe-host") && finding.probeHost && (
            <TsButton
              onClick={() => {
                requestProbe({
                  host: finding.probeHost as string,
                  ports: finding.probePorts,
                  siteId: finding.siteId ?? null,
                  site:
                    siteDetail && siteDetail.id === finding.siteId
                      ? siteDetail
                      : null,
                  cameraIds: finding.probeCameraIds,
                });
                // Connections has a site picker now (ENG-2058): open it on
                // the finding's site, not whatever it last showed.
                if (finding.siteId != null)
                  openTabForSite("connections", finding.siteId);
                else setTab("connections");
              }}
              data-testid={`finding-probe-${finding.id}`}
            >
              Check reachability
            </TsButton>
          )}
          {finding.actions.includes("fix-smtp") && finding.siteId != null && (
            <TsButton
              color={TS_COLORS.smtp}
              onClick={() =>
                openTabForSite("smtp-cameras", finding.siteId ?? null)
              }
            >
              Fix filename rule
            </TsButton>
          )}
          <TsButton variant="quiet" onClick={onCopy}>
            Copy diagnostic report
          </TsButton>
        </Box>
        {open && <AffectedTable finding={finding} />}
      </Box>
      <Box
        sx={{
          ...MULISH,
          textAlign: { xs: "left", md: "right" },
          gridColumn: { xs: "2", md: "auto" },
          color: TS_COLORS.ink3,
          fontSize: 13,
          whiteSpace: "nowrap",
        }}
      >
        <Box
          component="b"
          sx={{
            display: "block",
            fontSize: 22,
            color: TS_COLORS.navy,
            fontWeight: 800,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {finding.metric.value}
        </Box>
        {finding.metric.label}
      </Box>
    </Box>
  );
};

/**
 * Diagnosis = "are alarms getting through, and if not, which link failed?"
 *
 * Two surfaces only: what the monitoring API processed for the scope in the
 * last hour, and a symptom-led walkthrough that sends the customer to the
 * tab that proves each link. Verdicts computed from connector heartbeats and
 * camera video (the former "What we found" card) overlapped Camera Health and
 * are not shown here; the findings are still built for the copied report.
 */
/**
 * Open a tab from a symptom answer, carrying the site (or group) being
 * diagnosed. setTab restores that tab's own remembered scope, which dropped
 * the site (QA DX-07, ENG-2187).
 */
export const goToKeepingScope =
  (
    openTabForSite: (tab: TroubleshootingTab, siteId: number | null) => void,
    scope: string,
    siteId: number | null
  ) =>
  (tab: TroubleshootingTab) =>
    openTabForSite(tab, scope === "site" ? siteId : null);

export const DiagnosisTab = ({
  onCopyReport,
}: {
  onCopyReport: () => void;
}) => {
  const { symptom, setSymptom, alertsReceived, scope, siteId, openTabForSite } =
    useTroubleshootingStore();
  const scopeLabel = useScopeLabel();
  // "Not receiving any alarms" opens on the fact that matters first: whether
  // any alarms are flowing for this scope right now (ENG-1902).
  const alertsLead =
    alertsReceived?.status === "ok"
      ? alertsReceivedSentence(alertsReceived.data, scopeLabel)
      : null;
  const ctx: AnswerContext = {
    goTo: goToKeepingScope(openTabForSite, scope, siteId),
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 2.5, minWidth: 0 }}
    >
      <AlertsReceivedCard />
      <Card testId="symptom-card">
        <CardHeader
          title="Start from a symptom"
          hint="Tell us what you are seeing; each answer walks you through the checks, tab by tab."
          right={
            <TsButton
              variant="outline"
              size="sm"
              onClick={onCopyReport}
              data-testid="copy-diagnostic-report"
            >
              Copy diagnostic report
            </TsButton>
          }
        />
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", p: 2 }}>
          {SYMPTOMS.map((s) => {
            const on = symptom === s.id;
            return (
              <Box
                key={s.id}
                component="button"
                type="button"
                onClick={() => setSymptom(on ? null : s.id)}
                aria-pressed={on}
                sx={{
                  ...MULISH,
                  border: `1px solid ${on ? TS_COLORS.navy : TS_COLORS.line}`,
                  backgroundColor: on ? TS_COLORS.navy : "#fff",
                  color: on ? "#fff" : TS_COLORS.ink2,
                  borderRadius: "20px",
                  px: 2,
                  py: 1,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                {s.label}
              </Box>
            );
          })}
        </Box>
        {symptom && (
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <Typography
              component="div"
              sx={{
                ...MULISH,
                fontSize: 15,
                borderLeft: `3px solid ${TS_COLORS.navy}`,
                pl: 2,
                py: 0.5,
                maxWidth: "90ch",
              }}
            >
              {symptom === "none" && (
                <Box component="div" sx={{ mb: 1 }}>
                  {alertsReceived == null ? (
                    <Box component="span" data-testid="symptom-checking">
                      Still checking whether alerts are being processed…
                    </Box>
                  ) : alertsLead ? (
                    <Box component="span" data-testid="symptom-alerts-lead">
                      <b>{alertsLead}</b>
                    </Box>
                  ) : null}
                </Box>
              )}
              {answerFor(symptom, ctx)}
            </Typography>
          </Box>
        )}
      </Card>
    </Box>
  );
};
